/*
 * KzmCalAvailability — adapter from the availabilities endpoints to KzmCal's
 * `availability` option.
 *
 *   const cal = new KzmCal({
 *     mount: document.getElementById('mount'),
 *     minNights: 29,
 *     availability: KzmCalAvailability.create({
 *       url: '/de/raw-availabilities/891490',
 *       path: 'blocked', params: {}, timeSuffix: '',
 *     }),
 *   });
 *
 * Two endpoints, one contract. Both are read through `path`, so which one a
 * page talks to is a call-site decision.
 *
 *   /raw-availabilities/{id}   `blocked`: [{from, to}], inclusive ranges of
 *                              blocked nights, and nothing else. Preferred.
 *
 *   /availabilities/{id}       the legacy endpoint, from when check-in and
 *                              check-out were two separate pickers. Returns
 *                              four flat date lists:
 *
 *     occupiedDates.from       the blocked nights            <- the only input
 *     occupiedDates.to         the same nights shifted a day, as check-out days
 *     occupiedDatesExt.from    blocked nights with the minimum stay already
 *                              projected onto them, server-side
 *     occupiedDatesExt.to      always empty
 *
 * `occupiedDatesExt.from` is deliberately not consumed. KzmCal derives the
 * min-stay blocking itself from `minNights` (see `cellReason` in KzmCal.js),
 * and deriving is strictly more correct than reading the server's list: that
 * list is truncated to the requested window, so asking for a window shorter
 * than the minimum stay returns *no* blocked dates even though nothing in it
 * can be booked.
 *
 * The legacy endpoint also needs `n=1` — it is the app-wide "new frontend"
 * flag — and that flag makes it drop each booking's *arrival* night from
 * `occupiedDates.from`. The old picker never noticed, because it blocked
 * check-in on `occupiedDates.from ∪ occupiedDatesExt.from` and `Ext` carries
 * every arrival day. Reading the night list on its own, one night per booking
 * goes missing in each direction. That is what the raw endpoint exists to fix;
 * the legacy path is kept here for pages that have not moved yet, one night
 * short and all.
 */
(function (global) {
  'use strict';

  const DEFAULTS = {
    url: null,
    path: 'occupiedDates.from',
    params: { n: '1' },
    // The endpoint wants `2026-08-01T00:00:00`, not a bare date.
    timeSuffix: 'T00:00:00',
    fetch: null,
    requestInit: null,
  };

  function getPath(obj, path) {
    let cur = obj;
    for (const key of String(path).split('.')) {
      if (cur == null) return undefined;
      cur = cur[key];
    }
    return cur;
  }

  function buildUrl(base, fromISO, toISO, params, timeSuffix) {
    const href = (global.location && global.location.href) || undefined;
    const url = new URL(base, href);
    url.searchParams.set('from', fromISO + timeSuffix);
    url.searchParams.set('to', toISO + timeSuffix);
    for (const key of Object.keys(params || {})) {
      url.searchParams.set(key, params[key]);
    }
    return url.toString();
  }

  /**
   * Collapse a list of individual blocked nights into contiguous ranges.
   * Input may be in any order and may contain duplicates; `2026-08-02` and
   * `2026-08-02T00:00:00` are both accepted.
   */
  function toRanges(dates) {
    const CalDate = global.KzmCal && global.KzmCal.CalDate;
    if (!CalDate) throw new Error('KzmCalAvailability: KzmCal must be loaded first');

    const seen = new Set();
    const parsed = [];
    for (const raw of dates) {
      const d = CalDate.fromISO(raw);
      if (!d) continue;
      const key = d.toISOLocal();
      if (seen.has(key)) continue;
      seen.add(key);
      parsed.push(d);
    }
    parsed.sort((a, b) => a.time() - b.time());

    const ranges = [];
    let start = null;
    let prev = null;
    for (const d of parsed) {
      if (prev && prev.addDays(1).isSame(d)) {
        prev = d;
        continue;
      }
      if (start) ranges.push({ from: start.toISOLocal(), to: prev.toISOLocal() });
      start = d;
      prev = d;
    }
    if (start) ranges.push({ from: start.toISOLocal(), to: prev.toISOLocal() });
    return ranges;
  }

  /**
   * Build a function suitable for KzmCal's `availability` option.
   *
   * Options:
   *   url          endpoint, absolute or relative. An existing query string is
   *                preserved — `?encode=0` survives. May also be a function
   *                `(fromISO, toISO) => string` for full control.
   *   path         dotted path to the blocked-nights array in the response.
   *                Either shape is accepted: `[{from, to}]` ranges are passed
   *                through, bare date strings are collapsed into ranges.
   *                Default `occupiedDates.from`, the legacy endpoint.
   *   params       extra query parameters. Default `{ n: '1' }`, which the raw
   *                endpoint neither needs nor reads — pass `{}` there.
   *   timeSuffix   appended to the two ISO dates. Default `T00:00:00`.
   *   fetch        fetch implementation, for tests. Default `global.fetch`.
   *   requestInit  extra options merged into the fetch call.
   */
  function create(options) {
    const opt = Object.assign({}, DEFAULTS, options || {});
    if (!opt.url) throw new Error('KzmCalAvailability.create: options.url is required');
    const doFetch = opt.fetch || (global.fetch && global.fetch.bind(global));
    if (!doFetch) throw new Error('KzmCalAvailability.create: no fetch implementation available');

    return async function availability(fromISO, toISO) {
      const url = typeof opt.url === 'function'
        ? opt.url(fromISO, toISO)
        : buildUrl(opt.url, fromISO, toISO, opt.params, opt.timeSuffix);

      const init = Object.assign(
        { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } },
        opt.requestInit || {}
      );

      const res = await doFetch(url, init);
      if (!res.ok) {
        throw new Error(`KzmCalAvailability: ${res.status} ${res.statusText} for ${url}`);
      }
      const body = await res.json();
      if (body && body.success === false) {
        throw new Error('KzmCalAvailability: endpoint reported success:false');
      }

      // The raw endpoint clamps a window wider than 366 days. KzmCal records
      // the window it *asked* for as loaded, so a silent truncation would read
      // as "nothing is booked out there" and never be refetched.
      if (body && body.window && body.window.to && body.window.to < toISO) {
        console.warn(
          `KzmCalAvailability: asked for … ${toISO}, served … ${body.window.to}. ` +
          'Dates past the served end are unknown, not free.'
        );
      }

      const list = getPath(body, opt.path);
      // An empty list is a legitimate answer (nothing booked). A *missing* one
      // means the shape changed — surfacing that as an error is what keeps a
      // renamed field from silently unblocking the whole calendar.
      if (!Array.isArray(list)) {
        throw new Error(`KzmCalAvailability: no array at "${opt.path}" in the response`);
      }
      // Already ranges — hand them straight over. KzmCal parses the two ends
      // itself and drops anything malformed, so there is nothing to add here.
      if (list.length && typeof list[0] === 'object' && list[0] !== null) {
        return list;
      }
      return toRanges(list);
    };
  }

  global.KzmCalAvailability = { create, toRanges };
})(typeof window !== 'undefined' ? window : globalThis);
